<template>
  <v-container
    fill-height
    justify-content-start
    :class="{ 'pa-2': $vuetify.breakpoint.xsOnly }"
    class="app-section-width"
  >
    <v-layout column>
      <v-toolbar color="white" flat>
        <v-btn icon light @click="exit()">
          <v-icon color="grey darken-2">mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title class="grey--text text--darken-4 pa-0">{{getDashboardName({url: $route.path})}}</v-toolbar-title>
      </v-toolbar>

      <div class="pa-2">
        <v-tabs fixed-tabs v-model="tab">
          <v-tab v-for="item in items" :key="item.tab">
            {{ $t("reservations." + item.tab) }}
          </v-tab>
        </v-tabs>
      </div>

      <v-layout column class="app-scroll-section">
        <div
          class="justify-center"
          id="reservations"
          ref="reservations"
          v-if="reservations.length > 0 || loadingReservations"
          @scroll="scroll()"
          style="overflow-y: scroll; -webkit-overflow-scrolling: touch"
        >
          <v-layout justify-center>
            <v-btn
              class="ma-1"
              v-if="!loadingReservations"
              icon
              small
              @click="refreshReservations()"
            >
              <v-icon color="grey">refresh</v-icon>
            </v-btn>
          </v-layout>
          <v-layout row wrap ma-1>
            <v-flex
              xs12
              sm6
              v-for="item in reservations"
              :key="item.reservation.id"
            >
              <reservation-list-item
                :item="item"
                @openDetails="(item) => openDetails(item)"
              />
            </v-flex>
          </v-layout>

          <reservation-modal
            :openDetails="showDetailsDialog"
            :reservationId="reservationId"
            @closeDetails="
              showDetailsDialog = false;
              reservationId = null;
            "
            @reload="refreshReservations()"
          />

          <v-layout justify-center>
            <v-progress-circular
              v-if="loadingReservations"
              indeterminate
              color="secondary"
              class="my-2"
            ></v-progress-circular>
            <span v-if="reachedEnd" class="user-text body-2 mt-2 mb-2">{{
              $t("reservations.no_more_reservations")
            }}</span>
            <span
              v-if="!loadingReservations && !reachedEnd"
              class="user-text body-2 mt-2"
              @click="loadMoreReservations()"
              >{{ $t("reservations.show_more") }}</span
            >
          </v-layout>
        </div>
        <v-layout v-else column justify-center align-center class="grey--text">
          <v-btn
            v-if="!loadingReservations"
            icon
            small
            @click="refreshReservations()"
          >
            <v-icon color="grey">refresh</v-icon>
          </v-btn>
          {{ $t("reservations.no_reservations") }}
        </v-layout>
        <v-layout wrap class="justify-end" pr-2>
          <v-fade-transition mode="out-in">
          <v-btn
            v-if="reservations.length > 0 && scrolled"
            fab
            dark
            fixed
            small
            bottom
            color="primary"
            @click="goToTop()"
          >
            <v-icon dark>expand_less</v-icon>
          </v-btn>
          </v-fade-transition>
        </v-layout>
      </v-layout>
    </v-layout>
  </v-container>
</template>

<script>
import { debounce } from 'lodash'
import { mapGetters } from 'vuex'
import routeNames from '@/router/routes'
import router from '@/router'
import { activityReservationStatus } from '@/config'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'
import activityMixins from '@/mixins/Activity'
const activityReservationsService = new ActivityReservationsRequests()

export default {
  data () {
    return {
      loading: false,
      tab: null,
      items: [
        { tab: 'active', filterByStatus: [ activityReservationStatus.PENDING, activityReservationStatus.ACCEPTED, activityReservationStatus.PENDING_MODIFICATION ] },
        {
          tab: 'completed',
          filterByStatus: [ activityReservationStatus.REJECTED, activityReservationStatus.ATTENDED, activityReservationStatus.ABSENT, activityReservationStatus.CANCELLED ],
        },
      ],
      reservations: [],
      loadingReservations: false,
      offset: 0,
      limit: 10,
      reachedEnd: false,
      showDetailsDialog: false,
      reservationId: null,
      scrolled: false,
      debounceScrollHandler: null,
    }
  },
  components: {
    ReservationListItem: () =>
      import(
        '@/components/view-components/reservations/ReservationListItem.vue'
      ),
    ReservationModal: () =>
      import(
        '@/components/view-components/reservations/ReservationModal.vue'
      ),
  },
  created () {
    if (this.$route.query.reservationId) {
      this.openDetailsById(this.$route.query.reservationId)
      this.$store.commit('activityReservations/resetLastReservation')
    }
    this.$store.dispatch('activities/listActivities').then(() => {})
  },
  beforeDestroy () {
    if (this.$refs.reservations) {
      this.$refs.reservations.removeEventListener('scroll', this.debounceScrollHandler)
    }
  },
  computed: {
    ...mapGetters({
      getDashboardName: 'service/getDashboardName',
    }),
  },
  methods: {
    handleScroll (event) {
      var y = event.target.scrollTop
      if (y >= 200) {
        this.scrolled = true
      } else {
        this.scrolled = false
      }
    },
    exit () {
      router.push({ name: routeNames.main.name })
    },
    getReservations () {
      this.loadingReservations = true
      return new Promise(() => {
        activityReservationsService
          .listReservations({
            limit: this.limit,
            offset: this.offset,
            status: this.items[this.tab].filterByStatus,
            orderBy: this.tab === 0 ? 'ASC' : 'DESC',
          })
          .then(async (response) => {
            this.debounceScrollHandler = debounce(this.handleScroll, 100)
            this.$refs.reservations.addEventListener('scroll', this.debounceScrollHandler)
            this.loadingReservations = false
            if (response.reservations.length === 0) {
              this.reachedEnd = true
            } else {
              this.reservations.push.apply(
                this.reservations,
                response.reservations
              )
              if (!response.hasNext) {
                this.reachedEnd = true
              }
            }
          })
          .finally(() => {
            this.loadingReservations = false
          })
      })
    },
    refreshReservations () {
      this.offset = 0
      this.reachedEnd = false
      this.reservations = []
      this.getReservations()
    },
    loadMoreReservations () {
      this.offset += this.limit
      this.getReservations()
    },
    goToTop () {
      const scrollDuration = 400
      const reservationList = document.getElementById('reservations')
      var scrollStep = -reservationList.scrollTop / (scrollDuration / 15)
      var scrollInterval = setInterval(function () {
        if (reservationList.scrollTop !== 0) {
          reservationList.scrollBy(0, scrollStep)
        } else clearInterval(scrollInterval)
      }, 15)
    },
    scroll () {
      if (this.loadingReservations || this.reachedEnd) return
      const o = document.getElementById('reservations')
      if (o) {
        const bottomOfWindow = o.offsetHeight + o.scrollTop === o.scrollHeight
        if (bottomOfWindow && !this.loadingReservations && !this.reachedEnd) {
          this.loadMoreReservations()
        }
      }
    },
    openDetailsById (id) {
      this.reservationId = id
      this.showDetailsDialog = true
    },
    openDetails (item) {
      this.reservationId = item.reservation.id
      this.showDetailsDialog = true
    },
  },
  watch: {
    tab () {
      this.refreshReservations()
    },
  },
  mixins: [activityMixins],
}
</script>
